import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { I } from '../icons-ds';
import { Button, IconButton, SectionHeader, Tag, Empty, copyPlainText } from '../ui';
import { MobileNav, useViewport } from '../layout';
import { PhraseSection } from './PhraseSection';
import { NoteBuilder } from './NoteBuilder';
import { parseTemplate, countBlanks, renderBody } from '../lib/placeholders';
import { formatUpdated } from '../lib/freshness';
import { usePhrases } from '../lib/PhrasesContext';
import { useToast } from '../lib/ToastContext';
import { expandedTerms } from '../lib/search';

const COPY_MENU: CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 30,
  minWidth: 300, padding: 4, background: 'var(--bg-surface)',
  border: '1px solid var(--line-divider)', borderRadius: 'var(--r-lg)',
  boxShadow: 'var(--shadow-3)', overflow: 'hidden',
};
const COPY_ITEM: CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: '9px 11px', borderRadius: 'var(--r-md)', background: 'transparent',
  border: 0, cursor: 'pointer',
};

export function PhraseDetail() {
  const viewport = useViewport();
  const navigate = useNavigate();
  const params = useParams();
  const isMobile = viewport === 'mobile';

  const { getPhrase, toggleFavorite, recordRecent } = usePhrases();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const phrase = params.id ? getPhrase(params.id) : undefined;

  // Terms carried from the search screen — highlight them (incl. abbreviation
  // expansions) and jump to the first hit.
  const terms = useMemo(() => expandedTerms(searchParams.get('q') || ''), [searchParams]);
  const contentRef = useRef<HTMLDivElement>(null);

  // Log the note as recently used so it surfaces on Search and in ⌘K.
  useEffect(() => {
    if (phrase) recordRecent(phrase.id);
  }, [phrase?.id, recordRecent]); // eslint-disable-line react-hooks/exhaustive-deps

  const [building, setBuilding] = useState(false);
  const [copyMenu, setCopyMenu] = useState(false);
  const blanks = useMemo(
    () => (phrase ? phrase.sections.reduce((n, s) => n + countBlanks(parseTemplate(s.body)), 0) : 0),
    [phrase]
  );

  useEffect(() => {
    if (!terms.length) return;
    // Let react-markdown paint the highlighted marks, then centre the first one.
    const t = setTimeout(() => {
      const el = contentRef.current?.querySelector('.dp-hl');
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 80);
    return () => clearTimeout(t);
  }, [phrase?.id, terms]);

  if (!phrase) {
    return (
      <div style={{ minHeight: '100%', background: 'var(--bg-canvas)' }}>
        <Empty
          icon={<I.search size={28} />}
          title="Phrase not found"
          body="This phrase may have been moved or deleted."
          action={<Button variant="tinted" onClick={() => navigate('/search')}>Back to search</Button>}
        />
      </div>
    );
  }

  const copyMode = async (mode: 'epic' | 'plain') => {
    setCopyMenu(false);
    const text = phrase.sections
      .map((s) => `# ${s.title}\n\n${mode === 'epic' ? s.body : renderBody(s.body, {}, 'plain')}`)
      .join('\n\n');
    const ok = await copyPlainText(text);
    if (ok) showToast(mode === 'epic' ? 'Copied with Epic tokens' : 'Copied as plain text');
  };

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-canvas)' }}>
      {isMobile && (
        <MobileNav
          title={phrase.name}
          backTitle="Search"
          onBack={() => navigate(-1)}
          large={false}
          action={
            <button
              onClick={() => toggleFavorite(phrase.id)}
              aria-label="Favorite"
              style={{
                color: phrase.favorite ? 'var(--warning)' : 'var(--accent)',
                padding: 6, background: 'transparent', border: 0, cursor: 'pointer',
              }}
            >
              {phrase.favorite ? <I.starFill size={22} /> : <I.star size={22} />}
            </button>
          }
        />
      )}

      <div
        style={{
          padding: isMobile ? '0 16px 32px' : '8px 32px 48px',
          maxWidth: 820, margin: '0 auto',
        }}
      >
        {!isMobile && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 0 24px',
            }}
          >
            <Button variant="plain" icon={<I.chevL size={18} />} onClick={() => navigate(-1)} size="sm">
              Back to search
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <IconButton
                icon={phrase.favorite ? <I.starFill size={18} /> : <I.star size={18} />}
                label="Favorite"
                active={phrase.favorite}
                onClick={() => toggleFavorite(phrase.id)}
              />
            </div>
          </div>
        )}

        <div
          style={{
            paddingBottom: 24, borderBottom: '1px solid var(--line-divider)', marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <Tag kind={phrase.primaryTag}>{phrase.tags[0]}</Tag>
            {phrase.tags.slice(1).map((t) => (
              <Tag key={t} kind="neutral">{t}</Tag>
            ))}
            {phrase.updated && (
              <Tag kind="neutral" icon={<I.clock size={11} />}>Updated {formatUpdated(phrase.updated)}</Tag>
            )}
          </div>

          <div className="t-largetitle" style={{ marginBottom: 8 }}>{phrase.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              className="t-mono"
              style={{
                background: 'var(--accent-fill)', color: 'var(--accent)',
                padding: '3px 8px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              }}
            >
              {phrase.trigger}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
            {blanks > 0 && (
              <Button variant="primary" icon={<I.sparkles size={16} />} onClick={() => setBuilding(true)}>
                Fill in ({blanks})
              </Button>
            )}
            <div style={{ position: 'relative' }}>
              <Button
                variant={blanks > 0 ? 'secondary' : 'primary'}
                icon={<I.copy size={16} />}
                iconRight={<I.chevDown size={14} />}
                onClick={() => setCopyMenu((o) => !o)}
              >
                Copy full phrase
              </Button>
              {copyMenu && (
                <>
                  <div onClick={() => setCopyMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                  <div style={COPY_MENU}>
                    <button className="dp-menu-item" style={COPY_ITEM} onClick={() => copyMode('epic')}>
                      <div style={{ fontWeight: 600, color: 'var(--fg-primary)', fontSize: 14 }}>Copy for Epic</div>
                      <div className="t-caption1" style={{ color: 'var(--fg-tertiary)' }}>
                        Keeps {'***'}, {'{…}'} and {'@…@'} for SmartPhrase expansion
                      </div>
                    </button>
                    <button className="dp-menu-item" style={COPY_ITEM} onClick={() => copyMode('plain')}>
                      <div style={{ fontWeight: 600, color: 'var(--fg-primary)', fontSize: 14 }}>Copy as plain text</div>
                      <div className="t-caption1" style={{ color: 'var(--fg-tertiary)' }}>
                        Readable blanks, tips removed
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div ref={contentRef} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {phrase.sections.map((s, i) => (
            <PhraseSection
              key={i}
              section={s}
              highlight={terms}
              onCopied={() => showToast('Copied to clipboard')}
            />
          ))}
        </div>

        {phrase.references && phrase.references.length > 0 && (
          <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--line-divider)' }}>
            <SectionHeader>References</SectionHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {phrase.references.map((r, i) => (
                <div
                  key={i}
                  className="t-footnote"
                  style={{ color: 'var(--fg-secondary)', paddingLeft: 16, position: 'relative' }}
                >
                  <span style={{ position: 'absolute', left: 0, color: 'var(--fg-quaternary)' }}>·</span>{' '}
                  {r}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {building && <NoteBuilder phrase={phrase} onClose={() => setBuilding(false)} />}
    </div>
  );
}

export default PhraseDetail;
